import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowRight, ArrowLeft, Check, Crown, ExternalLink, Sparkles, Video, FileText, QrCode, Users, Globe2 } from 'lucide-react';

export const BASIC_FEATURES = ['AI-written company profile', 'Hero & additional video hosting', 'Products, services & key contacts', 'Email signature & QR code'];
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const SAMPLE_POINTS = [
  { icon: Video, text: 'A hero video that tells your story in under a minute' },
  { icon: FileText, text: 'An AI-written overview, products and services, ready for buyers' },
  { icon: Users, text: 'Key contacts and leadership, one tap to call or email' },
  { icon: QrCode, text: 'A QR code and email signature that lead straight to it' },
  { icon: Globe2, text: 'Optional: translation, event showcase, invitations and testimonials' },
];

const GOALS = [
  { id: 'credibility', label: 'Build credibility & a professional presence' },
  { id: 'buyers', label: 'Win international buyers & partners' },
  { id: 'events', label: 'Promote events, exhibitions & visits' },
  { id: 'all', label: 'All of these' },
];
const NEEDS = [
  { id: 'multi_market', label: 'We sell to more than one country', addon: 'translation' },
  { id: 'events', label: 'We attend or host events', addon: 'showcase' },
  { id: 'invitations', label: 'We invite guests to events', addon: 'event_invitations' },
  { id: 'testimonials', label: 'We want client video testimonials', addon: 'testimonials' },
  { id: 'connect', label: 'We look for business opportunities', addon: 'connect' },
];

export const recommendPlan = (goal, needs, pricing, addons) => {
  const keys = new Set(NEEDS.filter((n) => needs.includes(n.id)).map((n) => n.addon));
  if (goal === 'buyers') { keys.add('translation'); keys.add('connect'); }
  if (goal === 'events') { keys.add('showcase'); keys.add('event_invitations'); }
  const available = addons.filter((a) => keys.has(a.key)).map((a) => a.key);
  const basic = pricing.products.basic_1y.inr;
  const featuresTotal = addons.filter((a) => available.includes(a.key)).reduce((s, a) => s + a.price_inr, 0);
  const allAccess = pricing.products.all_access_1y.inr;
  if (goal === 'all' || basic + featuresTotal >= Math.round(allAccess * 5 / 6)) {
    return { product_type: 'all_access_1y', addons: [], reason: goal === 'all' ? 'You want everything, and All-Access includes every feature.' : `Your picks would cost ${inr(basic + featuresTotal)}. All-Access gives you everything for ${inr(allAccess)}.` };
  }
  if (available.length) {
    const names = addons.filter((a) => available.includes(a.key)).map((a) => a.name).join(', ');
    return { product_type: 'basic_1y', addons: available, reason: `Based on your answers we suggest adding ${names}.` };
  }
  return { product_type: 'basic_1y', addons: [], reason: 'Basic covers a complete professional profile. You can add features anytime.' };
};

const Shell = ({ step, children, onBack, onNext, nextLabel = 'Continue', nextDisabled, testId }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 md:p-8" data-testid={testId}>
    <p className="text-xs text-zinc-500 mb-4">Step {step} of 3 · Getting started</p>
    {children}
    <div className="flex justify-between mt-8 pt-6 border-t border-zinc-800">
      {onBack ? <button type="button" onClick={onBack} className="flex items-center gap-2 text-zinc-400 hover:text-white px-4 py-3" data-testid={`${testId}-back`}><ArrowLeft className="w-4 h-4" /> Back</button> : <div />}
      <button type="button" onClick={onNext} disabled={nextDisabled} className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white px-6 py-3 rounded-lg font-semibold" data-testid={`${testId}-next`}>{nextLabel} <ArrowRight className="w-4 h-4" /></button>
    </div>
  </div>
);

const SAMPLE_SLUGS = ['cinimage', 'brandstage', 'continex'];

export const useSampleSlug = () => {
  const [slug, setSlug] = useState(SAMPLE_SLUGS[0]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const s of SAMPLE_SLUGS) {
        try {
          await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/p/${s}`);
          if (!cancelled) setSlug(s);
          return;
        } catch { /* try next sample */ }
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return slug;
};

const SampleScreen = ({ onNext }) => {
  const slug = useSampleSlug();
  return (
  <Shell step={1} onNext={onNext} nextLabel="I'm ready, show me plans" testId="wizard-sample">
    <h2 className="text-2xl font-bold text-white mb-2">See what you'll get</h2>
    <p className="text-zinc-400 mb-6">This is a real BrandStage profile, built the same way yours will be. Scroll through it before you start.</p>
    <div className="grid md:grid-cols-[1fr_240px] gap-6">
      <div className="rounded-xl overflow-hidden border border-zinc-700 bg-black">
        <div className="flex items-center gap-1.5 px-3 py-2 bg-zinc-800 border-b border-zinc-700">
          <span className="w-2.5 h-2.5 rounded-full bg-zinc-600" /><span className="w-2.5 h-2.5 rounded-full bg-zinc-600" /><span className="w-2.5 h-2.5 rounded-full bg-zinc-600" />
          <span className="ml-3 text-[11px] text-zinc-400 truncate">brandstage.app/p/{slug}</span>
        </div>
        <iframe src={`/p/${slug}`} title="Sample BrandStage profile" className="w-full h-[460px] bg-zinc-950" loading="lazy" data-testid="wizard-sample-iframe" />
      </div>
      <div>
        <ul className="space-y-4">
          {SAMPLE_POINTS.map(({ icon: Icon, text }) => (
            <li key={text} className="flex gap-3 text-sm text-zinc-300"><Icon className="w-4 h-4 text-[#C41E3A] shrink-0 mt-0.5" />{text}</li>
          ))}
        </ul>
        <a href={`/p/${slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-6 text-sm text-white hover:text-[#C41E3A]" data-testid="wizard-sample-open">Open full sample <ExternalLink className="w-3.5 h-3.5" /></a>
      </div>
    </div>
  </Shell>
  );
};

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`text-left px-4 py-3 rounded-lg border text-sm transition-colors flex items-center gap-3 ${active ? 'border-[#C41E3A] bg-[#C41E3A]/10 text-white' : 'border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}>
    <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${active ? 'border-[#C41E3A] bg-[#C41E3A]' : 'border-zinc-600'}`}>{active && <Check className="w-3 h-3 text-white" />}</span>
    {children}
  </button>
);

const GoalsScreen = ({ goal, setGoal, needs, setNeeds, onBack, onNext }) => (
  <Shell step={2} onBack={onBack} onNext={onNext} nextDisabled={!goal} nextLabel="See my recommendation" testId="wizard-goals">
    <h2 className="text-2xl font-bold text-white mb-2">What do you want your profile to do?</h2>
    <p className="text-zinc-400 mb-6">Two quick questions so we can recommend the right plan. Nothing is charged yet.</p>
    <p className="text-sm text-zinc-300 font-medium mb-3">1. Your main goal</p>
    <div className="grid sm:grid-cols-2 gap-3 mb-8">
      {GOALS.map((g) => <Chip key={g.id} active={goal === g.id} onClick={() => setGoal(g.id)} testId={`wizard-goal-${g.id}`}>{g.label}</Chip>)}
    </div>
    <p className="text-sm text-zinc-300 font-medium mb-3">2. Which of these apply to you? <span className="text-zinc-500 font-normal">(tick any)</span></p>
    <div className="grid sm:grid-cols-2 gap-3">
      {NEEDS.map((n) => (
        <Chip key={n.id} active={needs.includes(n.id)} onClick={() => setNeeds(needs.includes(n.id) ? needs.filter((x) => x !== n.id) : [...needs, n.id])} testId={`wizard-need-${n.id}`}>{n.label}</Chip>
      ))}
    </div>
  </Shell>
);

export const PlanPicker = ({ pricing, addons, plan, setPlan, recommended }) => {
  const isBasic = plan.product_type !== 'all_access_1y';
  const toggle = (key) => setPlan({ ...plan, addons: plan.addons.includes(key) ? plan.addons.filter((k) => k !== key) : [...plan.addons, key] });
  const card = (active, onClick, testId, children, rec) => (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`relative text-left rounded-xl border p-5 transition-colors ${active ? 'border-[#C41E3A] bg-[#C41E3A]/10' : 'border-zinc-700 hover:border-zinc-500'}`}>
      {rec && <span className="absolute -top-2.5 left-4 text-[10px] uppercase tracking-wider bg-[#C41E3A] text-white px-2 py-0.5 rounded-full">Recommended</span>}
      {children}
    </button>
  );
  return (
    <div>
      <div className="grid md:grid-cols-2 gap-4">
        {card(isBasic, () => setPlan({ ...plan, product_type: plan.product_type === 'all_access_1y' ? 'basic_1y' : plan.product_type }), 'plan-option-basic', (
          <>
            <p className="text-white font-semibold">Basic{plan.addons.length ? ' + selected features' : ''}</p>
            <p className="text-2xl font-bold text-white mt-1">{inr(pricing.products.basic_1y.inr)}<span className="text-sm font-normal text-zinc-500"> /year</span></p>
            <p className="text-xs text-zinc-500 mb-3">or {inr(pricing.products.basic_6m.inr)} for 6 months · + GST</p>
            <ul className="space-y-1.5">{BASIC_FEATURES.map((f) => <li key={f} className="text-xs text-zinc-400 flex gap-2"><Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />{f}</li>)}</ul>
          </>
        ), recommended?.product_type !== 'all_access_1y')}
        {card(!isBasic, () => setPlan({ product_type: 'all_access_1y', addons: [] }), 'plan-option-all-access', (
          <>
            <p className="text-white font-semibold flex items-center gap-2"><Crown className="w-4 h-4 text-amber-400" /> All-Access</p>
            <p className="text-2xl font-bold text-white mt-1">{inr(pricing.products.all_access_1y.inr)}<span className="text-sm font-normal text-zinc-500"> /year</span></p>
            <p className="text-xs text-zinc-500 mb-3">Everything in Basic + every Pro feature · + GST</p>
            <ul className="space-y-1.5">{addons.map((a) => <li key={a.key} className="text-xs text-zinc-400 flex gap-2"><Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />{a.name}</li>)}</ul>
          </>
        ), recommended?.product_type === 'all_access_1y')}
      </div>
      {isBasic && (
        <div className="mt-6" data-testid="plan-addon-list">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-zinc-300 font-medium">Add Pro features <span className="text-zinc-500 font-normal">({inr(addons[0]?.price_inr)} each, 12 months)</span></p>
            <div className="flex gap-1 text-xs bg-zinc-800 rounded-full p-1">
              {['basic_6m', 'basic_1y'].map((p) => (
                <button key={p} type="button" onClick={() => setPlan({ ...plan, product_type: p })} className={`px-3 py-1 rounded-full ${plan.product_type === p ? 'bg-zinc-600 text-white' : 'text-zinc-400'}`} data-testid={`plan-duration-${p}`}>{p === 'basic_6m' ? '6 months' : '1 year'}</button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {addons.map((a) => (
              <label key={a.key} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${plan.addons.includes(a.key) ? 'border-[#C41E3A] bg-[#C41E3A]/5' : 'border-zinc-700'}`} data-testid={`plan-addon-${a.key}`}>
                <input type="checkbox" className="mt-0.5 accent-[#C41E3A]" checked={plan.addons.includes(a.key)} onChange={() => toggle(a.key)} />
                <span><span className="text-sm text-white block">{a.name}{recommended?.addons?.includes(a.key) && <span className="ml-2 text-[10px] text-[#C41E3A]">suggested</span>}</span><span className="text-xs text-zinc-500">{a.description}</span></span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const planTotal = (plan, pricing, addons) => {
  const base = pricing.products[plan.product_type].inr;
  const extra = plan.product_type === 'all_access_1y' ? 0 : addons.filter((a) => plan.addons.includes(a.key)).reduce((s, a) => s + a.price_inr, 0);
  return base + extra;
};

export default function PlanWizard({ pricing, addons, plan, setPlan, onDone, startAt = 'sample' }) {
  const [screen, setScreen] = useState(startAt);
  const [goal, setGoal] = useState('');
  const [needs, setNeeds] = useState([]);
  const [recommended, setRecommended] = useState(null);

  const toPlan = () => {
    const rec = recommendPlan(goal, needs, pricing, addons);
    setRecommended(rec);
    setPlan({ product_type: rec.product_type, addons: rec.addons });
    setScreen('plan');
  };

  if (screen === 'sample') return <SampleScreen onNext={() => setScreen('goals')} />;
  if (screen === 'goals') return <GoalsScreen goal={goal} setGoal={setGoal} needs={needs} setNeeds={setNeeds} onBack={() => setScreen('sample')} onNext={toPlan} />;

  const total = planTotal(plan, pricing, addons);
  const allAccess = pricing.products.all_access_1y.inr;
  return (
    <Shell step={3} onBack={() => setScreen('goals')} onNext={onDone} nextLabel="Start building my profile" testId="wizard-plan">
      <h2 className="text-2xl font-bold text-white mb-2">Your recommended plan</h2>
      {recommended && <p className="text-sm text-amber-300 flex items-start gap-2 mb-6" data-testid="wizard-reason"><Sparkles className="w-4 h-4 shrink-0 mt-0.5" />{recommended.reason}</p>}
      <PlanPicker pricing={pricing} addons={addons} plan={plan} setPlan={setPlan} recommended={recommended} />
      <div className="mt-6 rounded-lg bg-zinc-800/60 p-4 flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-zinc-300">Selected: <strong className="text-white" data-testid="wizard-total">{inr(total)}</strong> + GST</span>
        {plan.product_type !== 'all_access_1y' && total >= Math.round(allAccess * 5 / 6) && (
          <button type="button" onClick={() => setPlan({ product_type: 'all_access_1y', addons: [] })} className="text-sm text-amber-300 hover:text-amber-200" data-testid="wizard-switch-all-access">Get everything with All-Access for {inr(allAccess)} →</button>
        )}
      </div>
      <p className="text-xs text-zinc-500 mt-3">You pay only at the end, after previewing your profile. You can change your plan at any time before paying.</p>
    </Shell>
  );
}
